import { ReactNode, useCallback } from 'react'

import {
  IconDefinition,
  faBriefcase,
  faBuilding,
  faCalendar,
  faCalendarExclamation,
  faCircle,
  faEnvelope,
  faFax,
  faIdCard,
  faLocationPin,
  faPhone,
  faUser,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Flex, Tag } from 'antd'
import isNil from 'lodash.isnil'
import styled from 'styled-components'

import CodeBookItemFieldDisplay from 'src/components/Field/CodeBookItemFieldDisplay'
import FieldGroupFieldDisplay from 'src/components/Field/FieldGroupFieldDisplay'
import { ForeignValueDisplay } from 'src/components/ForeignValues/ForeignValueDisplay'

import { filterNil, formatPhoneNumber, formatUserName } from '../../utils'
import { DateFormats, formatDate } from '../../utils/date'
import { FieldDisplayInput } from '../../utils/field'
import { formatNumber, NumberFormat } from '../../utils/number'
import CSLBContractorStatusDisplay from '../CSLBContractor/CSLBContractorStatusDisplay'
import MassachusettsContractorStatusDisplay from '../MassachusettsContractor/MassachusettsContractorStatusDisplay'
import {
  getMaContractorDisplayName,
  getMaContractorFormattedAddress,
} from '../MassachusettsContractor/util'
import RecordIdLinkCell from '../RecordIdLinkCell'
import { RenderedSignature } from '../RenderedSignature'
import Text, { TextSize } from '../Typography/Text'

import EntityVersionFieldDisplay from './EntityVersionFieldDisplay'
import { FileFieldDisplay } from './FileFieldDisplay'
import { formatFieldContactAddress } from './util'

type Props = {
  input: FieldDisplayInput
  showDashAsFallback?: boolean
  size?: TextSize
  renderImages?: boolean
  renderPDFsInline?: boolean
}

const Vertical = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const makeIcon = (icon: IconDefinition) => (
  <FontAwesomeIcon icon={icon} style={{ marginRight: '8px' }} />
)

// the backend has related Field display code for documents in stringifyFormFieldInput
export const FieldDisplay = (props: Props) => {
  const {
    input: {
      type,
      checked,
      codeBookItemIds,
      date,
      num,
      str,
      strs,
      record,
      inspectionResult,
      contact,
      cslbContractorSnapshot,
      massachusettsContractorSnapshot,
      file,
      files,
      recordCollections,
      violationTypes,
      foreignValue,
      userHasAccessToForeignValue,
      entityVersions,
      childFieldGroups,
      childFieldGroupTemplateId,
      users,
    },
    showDashAsFallback = true,
    size,
    renderImages = false,
    renderPDFsInline = false,
  } = props
  const Fallback = useCallback(
    () => (showDashAsFallback ? <Text size={size}>-</Text> : null),
    [showDashAsFallback, size]
  )
  const ValueText = useCallback(
    ({ children }: { children: React.ReactNode }) => (
      <Text whiteSpace="pre-line" size={size}>
        {children}
      </Text>
    ),
    [size]
  )
  const renderLine = useCallback(
    (icon: IconDefinition, val?: ReactNode) =>
      val ? (
        <ValueText>
          {makeIcon(icon)}
          {val}
        </ValueText>
      ) : (
        ''
      ),
    [ValueText]
  )
  switch (type) {
    case 'Address':
    case 'LongText':
    case 'Radio': {
      return str ? <ValueText>{str}</ValueText> : <Fallback />
    }
    case 'Checkbox': {
      return <ValueText>{checked ? 'Yes' : 'No'}</ValueText>
    }
    case 'CodeBookItem': {
      return codeBookItemIds?.length ? (
        <CodeBookItemFieldDisplay codeBookItemIds={codeBookItemIds} size={size} />
      ) : (
        <Fallback />
      )
    }
    case 'Contact': {
      if (!contact || Object.keys(contact).every((k) => !contact[k as keyof typeof contact])) {
        return <Fallback />
      }
      const { firstName, lastName, email, phoneNumber, companyName, faxNumber, title } = contact

      const name = [firstName, lastName]
        .filter((a) => !!a)
        .map((a) => a?.trim())
        .join(' ')

      const formattedAddress = formatFieldContactAddress(contact)
      return (
        <Flex vertical>
          {renderLine(faUser, name)}
          {renderLine(faBuilding, companyName ?? '')}
          {renderLine(faIdCard, title ?? '')}
          {phoneNumber && renderLine(faPhone, formatPhoneNumber(phoneNumber))}
          {faxNumber && renderLine(faFax, formatPhoneNumber(faxNumber))}
          {email && renderLine(faEnvelope, email)}
          {formattedAddress && renderLine(faLocationPin, formattedAddress)}
        </Flex>
      )
    }
    case 'CSLBContractorSnapshot': {
      if (!cslbContractorSnapshot) {
        return <Fallback />
      }
      return (
        <Flex vertical>
          {renderLine(faUser, cslbContractorSnapshot.displayName)}
          {renderLine(faIdCard, cslbContractorSnapshot.licenseNumber)}
          {renderLine(
            faCircle,
            <>
              Status:{' '}
              <CSLBContractorStatusDisplay
                status={cslbContractorSnapshot.status}
                secondaryStatus={cslbContractorSnapshot.secondaryStatus}
              />
            </>
          )}
          {renderLine(faPhone, formatPhoneNumber(cslbContractorSnapshot.phoneNumber))}
          {renderLine(faLocationPin, cslbContractorSnapshot.address)}
          {renderLine(faCalendar, `Issued ${cslbContractorSnapshot.issueDate}`)}
          {renderLine(faCalendarExclamation, `Expires ${cslbContractorSnapshot.expirationDate}`)}
        </Flex>
      )
    }
    case 'MassachusettsContractorSnapshot': {
      if (!massachusettsContractorSnapshot) {
        return <Fallback />
      }
      const displayName = getMaContractorDisplayName(massachusettsContractorSnapshot)
      const formattedAddress = getMaContractorFormattedAddress(massachusettsContractorSnapshot)
      return (
        <Flex vertical>
          {renderLine(faUser, displayName)}
          {renderLine(faIdCard, `License #${massachusettsContractorSnapshot.number}`)}
          {renderLine(faBuilding, massachusettsContractorSnapshot.boardName)}
          {renderLine(faBriefcase, massachusettsContractorSnapshot.typeName)}
          {renderLine(
            faCircle,
            <>
              Status:{' '}
              <MassachusettsContractorStatusDisplay
                valid={massachusettsContractorSnapshot.valid}
                status={massachusettsContractorSnapshot.status}
              />
            </>
          )}
          {renderLine(faLocationPin, formattedAddress)}
          {massachusettsContractorSnapshot.issued &&
            renderLine(
              faCalendar,
              `Issued ${formatDate(massachusettsContractorSnapshot.issued, DateFormats.MonthNameDateNoTime)}`
            )}
          {massachusettsContractorSnapshot.expires &&
            renderLine(
              faCalendarExclamation,
              `Expires ${formatDate(massachusettsContractorSnapshot.expires, DateFormats.MonthNameDateNoTime)}`
            )}
        </Flex>
      )
    }
    case 'Currency': {
      return !isNil(num) && !isNaN(num) ? (
        <ValueText>{formatNumber(num, { format: NumberFormat.Currency })}</ValueText>
      ) : (
        <Fallback />
      )
    }
    case 'Date': {
      const val = date ?? str // TODO(GOV-96): Remove fallback once migration is done
      return val ? (
        <ValueText>{formatDate(val, DateFormats.MonthNameDateNoTime)}</ValueText>
      ) : (
        <Fallback />
      )
    }
    case 'Dropdown': {
      return strs?.length ? <ValueText>{strs.join(', ')}</ValueText> : <Fallback />
    }
    case 'File': {
      if (!file) {
        return <Fallback />
      }
      return (
        <FileFieldDisplay
          file={file}
          renderImages={renderImages}
          renderPDFsInline={renderPDFsInline}
        />
      )
    }
    case 'Files': {
      if (!files?.length) {
        return <Fallback />
      }
      return (
        <Vertical>
          {filterNil(files).map((f) => (
            <FileFieldDisplay
              file={f}
              key={`file-${f.id}`}
              renderImages={renderImages}
              renderPDFsInline={renderPDFsInline}
            />
          ))}
        </Vertical>
      )
    }
    case 'ForeignValue': {
      return foreignValue ? (
        <ForeignValueDisplay
          id={foreignValue.id}
          size={size}
          type={foreignValue.type}
          userHasAccessToForeignValue={!!userHasAccessToForeignValue}
        />
      ) : (
        <Fallback />
      )
    }
    case 'InspectionResult': {
      if (!inspectionResult) {
        return <Fallback />
      }
      return (
        <Tag color={inspectionResult.isPassing ? 'green' : 'red'} style={{ width: 'fit-content' }}>
          {inspectionResult.name}
        </Tag>
      )
    }
    case 'Number': {
      return !isNil(num) && !isNaN(num) ? <ValueText>{num}</ValueText> : <Fallback />
    }
    case 'Record': {
      return record?.id ? <RecordIdLinkCell id={record.id} size={size} /> : <Fallback />
    }
    case 'RecordCollection': {
      if (!recordCollections?.length) {
        return <Fallback />
      }
      return (
        <Flex vertical>
          {recordCollections.map((rc) => (
            <ValueText key={`rc-${rc?.id}`}>{rc?.name}</ValueText>
          ))}
        </Flex>
      )
    }
    case 'ViolationType': {
      if (!violationTypes?.length) {
        return <Fallback />
      }
      return (
        <Flex vertical>
          {violationTypes.map((vt) => (
            <ValueText key={`vt-${vt.id}`}>{vt.name}</ValueText>
          ))}
        </Flex>
      )
    }
    case 'ShortText': {
      const strings = [...(strs?.length ? strs : [str])].filter((s) => !!s?.trim())
      if (!strings.length) {
        return <Fallback />
      }
      return (
        <Flex vertical>
          {strings.map((string, index) => (
            <ValueText key={index}>{string}</ValueText>
          ))}
        </Flex>
      )
    }
    case 'Signature': {
      const url = file?.url?.signedUrl
      if (!url) {
        return <Fallback />
      }
      return <RenderedSignature size={size} type="modal" url={file?.url?.signedUrl ?? ''} />
    }
    case 'EntityVersion': {
      const ids = entityVersions?.flatMap((v) => v.id || []) ?? []
      const strings: string[] = []
      if (!ids.length && !!str) {
        // TODO [GOV-2986]: Remove the extra check on EntityVersion length
        // Single address fields with location snapshots converted to entity version fields have an str populated that duplicates the entity version
        strings.push(str)
      }
      strings.push(...filterNil(strs || []))

      if (!ids.length && !strings.length) {
        return <Fallback />
      }
      return (
        <Flex vertical>
          {ids.length ? <EntityVersionFieldDisplay entityVersionIds={ids} /> : null}
          {strings.map((s, idx) => (
            <Text italic key={`ev-str-${idx}`}>
              {s}
            </Text>
          ))}
        </Flex>
      )
    }
    case 'FieldGroup': {
      return (
        <FieldGroupFieldDisplay
          childFieldGroupTemplateId={childFieldGroupTemplateId}
          childFieldGroups={childFieldGroups}
        />
      )
    }
    case 'User': {
      if (!users?.length) {
        return <Fallback />
      }
      return (
        <Flex vertical>
          {users.map((u) => (
            <ValueText key={u.uuid}>{formatUserName(u)}</ValueText>
          ))}
        </Flex>
      )
    }
  }
}
